// src/lib/endpointApi.js
//
// G360_K541 - what Endpoint Governance and Deploy Policies read beside
// their own tables: the device spine, every source's record of each
// device, the guard's heartbeats, AI software found on endpoints, the
// prevention policies and what they did, and the rollout evidence. Every
// read names the organisation on screen and returns { data, error }; the
// pages say "not read" for an error, never "none".

import { supabase } from '@govern360/shared';

const PAGE = 1000;
async function pages(build, limit) {
  const rows = [];
  for (let from = 0; from < (limit || 20000); from += PAGE) {
    let res;
    try { res = await build().range(from, from + PAGE - 1); } catch (e) { return { data: null, error: e }; }
    if (res.error) return { data: null, error: res.error };
    const got = res.data || [];
    for (let i = 0; i < got.length; i += 1) rows.push(got[i]);
    if (got.length < PAGE) return { data: rows, error: null };
  }
  rows.capped = true;
  return { data: rows, error: null };
}
const none = () => Promise.resolve({ data: [], error: null });

/* The reconciled device spine: sources, collector findings, exposure band
   and coverage gaps per device (device_inventory, organisation named). */
export function listDevices(tenantId) {
  if (!tenantId) return none();
  return pages(() => supabase.rpc('device_inventory', { p_tenant: tenantId }).order('id', { ascending: true }));
}

/* Each source's own record of each device - compliance, last check-in,
   primary user - kept per source so they are never blended. */
export function listDeviceSources(tenantId) {
  if (!tenantId) return none();
  return pages(() => supabase.from('device_source')
    .select('id, device_id, source, source_key, source_name, raw, first_seen, last_seen')
    .eq('tenant_id', tenantId).order('id', { ascending: true }));
}

/* The guard's heartbeats. A per-user install names a person; a managed
   install names the device label it was given. */
// G360_K740 - guard 0.1.8 also reports how it was installed, where it updates from, the computer name in its
// policy and the profile's work account. Before the server has those columns, the old columns are read.
const HB_COLS = 'user_id, user_email, ext_version, browser, sites, first_seen, last_seen';
const HB_COLS_V2 = HB_COLS + ', install_type, update_source, device_name, profile_email';
export async function listHeartbeats(tenantId) {
  if (!tenantId) return none();
  const run = (cols) => pages(() => supabase.from('extension_heartbeats')
    .select(cols)
    .eq('tenant_id', tenantId).order('user_id', { ascending: true }));
  let r = await run(HB_COLS_V2);
  if (r.error && (String(r.error.code || '') === '42703' || /column/i.test(String(r.error.message || '')))) r = await run(HB_COLS);
  if (r.error || !r.data) return r;
  // G360_K741 - each install's backend match (automatic network match, a likely device, or why not), when the
  // database has it. Matching is extra: the heartbeats stand without it.
  try {
    const m = await pages(() => supabase.from('guard_device_match')
      .select('install_id, state, device_id, hours, only_hours, browser_hours, next_hours, next_only_hours, candidates, computed_at')
      .eq('tenant_id', tenantId).order('install_id', { ascending: true }));
    if (!m.error && m.data) {
      const by = {};
      m.data.forEach((x) => { by[String(x.install_id)] = x; });
      r.data.forEach((h) => { const x = by[String(h.user_id)]; if (x) h.match = x; });
    }
  } catch (e) { /* no matching yet */ }
  return r;
}

/* G360_K688 - installs an owner or admin linked to a device by hand. */
export function listInstallLinks(tenantId) {
  if (!tenantId) return none();
  return pages(() => supabase.from('endpoint_install_link')
    .select('install_id, device_id, linked_by_email, linked_at')
    .eq('tenant_id', tenantId).order('install_id', { ascending: true }));
}
export async function linkInstall(tenantId, installId, deviceId) {
  const { data, error } = await supabase.rpc('endpoint_link_install', { p_tenant: tenantId, p_install: installId, p_device: deviceId });
  if (error) throw error;
  return data || { ok: false };
}
export async function unlinkInstall(tenantId, installId) {
  const { data, error } = await supabase.rpc('endpoint_unlink_install', { p_tenant: tenantId, p_install: installId });
  if (error) throw error;
  return data || { ok: false };
}

/* AI apps Intune reports as installed (tenant level: a count of devices,
   not which ones). */
export function listEndpointAiTools(tenantId) {
  if (!tenantId) return none();
  return pages(() => supabase.from('endpoint_ai_tools')
    .select('id, tool_key, tool_name, source, device_count, versions, first_seen, last_seen, synced_at')
    .eq('tenant_id', tenantId).order('id', { ascending: true }));
}

/* What the endpoint collector found, item by item. */
export function listCollectorItems(tenantId) {
  if (!tenantId) return none();
  return pages(() => supabase.from('endpoint_inventory_item')
    .select('id, device_key, kind, name, vendor, version, detail, first_seen, last_seen')
    .eq('tenant_id', tenantId).order('id', { ascending: true }), 50000);
}

/* Prevention policies - what the guard is told to do. */
export function listPreventionPolicies(tenantId) {
  if (!tenantId) return none();
  return pages(() => supabase.from('policies')
    .select('id, name, action, mode, enabled, category, priority, hit_count, detector_keys, scope_type, updated_at')
    .eq('tenant_id', tenantId).order('id', { ascending: true }));
}

/* G360_K822 - devices a tenant has put out of scope for the browser
   guard. The global "operating system is Server" rule lives in
   endpointModel and is deliberately NOT stored, so this returns only the
   rules somebody chose. An error here must not blank the page: the model
   falls back to rule 1 alone, which is the safe direction - fewer
   exclusions, never more. */
export async function listScopeRules(tenantId) {
  if (!tenantId) return none();
  const { data, error } = await supabase.rpc('device_scope_rules', { p_tenant: tenantId });
  return { data: data || [], error };
}
/* G360_K825 - add or change one rule. These THROW rather than returning an
   error, like linkInstall above, because 0421 raises messages written for
   the person reading them ("A pattern of only wildcards would exclude every
   device"); the caller shows what came back instead of inventing wording of
   its own. Writing needs owner or admin, checked in the database, not here. */
export async function setScopeRule(tenantId, kind, value, note, enabled) {
  const { data, error } = await supabase.rpc('device_scope_rule_set', {
    p_tenant: tenantId, p_kind: kind, p_value: value,
    p_note: note || null, p_enabled: enabled !== false,
  });
  if (error) throw error;
  return data;
}
export async function deleteScopeRule(tenantId, ruleId) {
  const { data, error } = await supabase.rpc('device_scope_rule_delete', { p_tenant: tenantId, p_id: ruleId });
  if (error) throw error;
  return data;
}

/* Enforcement events in the window - what the guard did. */
export function listRecentEvents(tenantId, days) {
  if (!tenantId) return none();
  const since = new Date(Date.now() - (days || 30) * 864e5).toISOString();
  return pages(() => supabase.from('enforcement_events')
    .select('id, created_at, tool_name, user_email, action, direction, detector_keys')
    .eq('tenant_id', tenantId).gte('created_at', since).order('id', { ascending: true }), 50000);
}

/* AI Inventory, to say whether an app found on endpoints is governed. */
export function listInventoryTools(tenantId) {
  if (!tenantId) return none();
  return pages(() => supabase.from('ai_tools')
    .select('id, name, vendor, status, category')
    .eq('tenant_id', tenantId).order('id', { ascending: true }));
}

/* Rollout evidence for every deployment policy in the organisation. */
export function listDeployBindings(tenantId) {
  if (!tenantId) return none();
  return pages(() => supabase.from('deploy_group_bindings')
    .select('id, policy_id, kind, entra_group_id, display_name, member_count, synced_at')
    .eq('tenant_id', tenantId).order('id', { ascending: true }));
}
export function listValidationSnapshots(tenantId) {
  if (!tenantId) return none();
  return pages(() => supabase.from('deploy_validation_snapshots')
    .select('id, policy_id, in_scope, excluded, installed, pending, failed, not_checked_in, source, captured_at')
    .eq('tenant_id', tenantId).order('captured_at', { ascending: false }), 5000);
}

/* Native enforcement planes: Intune read back by API, Purview attested. */
export async function listPlaneStatus(tenantId) {
  if (!tenantId) return { data: [], error: null };
  try {
    const { data, error } = await supabase.from('enforcement_plane_status')
      .select('plane, state, readback, readback_at, marked_at, marked_by_email, last_checked_at, note')
      .eq('tenant_id', tenantId);
    if (error) return { data: null, error };
    return { data: data || [], error: null };
  } catch (e) { return { data: null, error: e }; }
}

/* posture_score for the organisation on screen: members, and members whose
   guard reported in the last 24 hours - the AI Exposure Score's endpoint
   coverage input. */
export async function endpointPosture(tenantId) {
  if (!tenantId) return { data: null, error: null };
  try {
    const { data, error } = await supabase.rpc('posture_score', { p_tenant: tenantId });
    if (error) return { data: null, error };
    return { data: (Array.isArray(data) ? data[0] : data) || null, error: null };
  } catch (e) { return { data: null, error: e }; }
}
